import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import * as actionTypes from "./actionTypes";
import {
  getCoursesCoursesMasterAPI,
  getCourseCoursesMasterAPI,
  createCourseCoursesMasterAPI,
  updateCourseCoursesMasterAPI,
  deleteCourseCoursesMasterAPI,
  getCourseModulesCoursesMasterAPI,
  getCourseActivitiesCoursesMasterAPI,
  createCourseModuleCoursesMasterAPI,
  updateCourseModuleCoursesMasterAPI,
  deleteCourseModuleCoursesMasterAPI,
  createCourseActivityCoursesMasterAPI,
  updateCourseActivityCoursesMasterAPI,
  deleteCourseActivityCoursesMasterAPI,
  getCourseActivityCoursesMasterAPI,
  duplicateCourseCoursesMasterAPI,
  exportCourseCoursesMasterAPI,
  importCourseCoursesMasterAPI,
  getCourseTreeCoursesMasterAPI,
} from "@icarius-connection/api";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { downloadBlob } from "@icarius-utils/download";
import { getFileExtension, getFileName } from "@icarius-utils/fileUpload";

const errorHandler = (e, dispatch, variant) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "IN_USE":
      errorString = (() => {
        if (variant === "course") return getLocalizedString("courseInUse");
        if (variant === "module") return getLocalizedString("moduleInUse");
        if (variant === "activity") return getLocalizedString("activityInUse");
        return "";
      })();
      break;
    case "DUPLICATED_DATA":
      errorString = (() => {
        if (variant === "course") return getLocalizedString("courseDuplicatedData");
        if (variant === "module") return getLocalizedString("moduleDuplicatedData");
        if (variant === "activity") return getLocalizedString("activityDuplicatedData");
        return "";
      })();
      break;
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = e.response?.data?.error === "INVALID_EXTENSION" ? getLocalizedString("invalidFileExtension") : getLocalizedString("validationError");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

// COURSE ///////////////////////////////////////////////////////////////////////////

export const getCoursesCoursesMasterAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ALL_COURSES });
  try {
    let response = await getCoursesCoursesMasterAPI();

    let courses = response.data?.courses;
    let skillList = response.data?.skillList;
    let userFieldsDefinition = response.data?.userFieldsDefinition;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ALL_COURSES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ALL_COURSES_FULFILLED,
      payload: {
        skillList,
        courses,
        userFieldsDefinition,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ALL_COURSES_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const getCourseCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_COURSE });
  try {
    let response = await getCourseCoursesMasterAPI(courseId);

    let course = response.data?.course;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_COURSE_FULFILLED,
      payload: {
        course,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const getCourseTreeCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_COURSE_TREE });
  try {
    let response = await getCourseTreeCoursesMasterAPI(courseId);

    let courseTree = response.data?.data;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_COURSE_TREE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_COURSE_TREE_FULFILLED,
      payload: {
        courseTree,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_COURSE_TREE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const createCourseCoursesMasterAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_COURSE });
  try {
    let response = await createCourseCoursesMasterAPI(dataToSend);

    let courses = response.data?.courses;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_COURSE_FULFILLED,
      payload: {
        courses,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const updateCourseCoursesMasterAction = (courseId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_COURSE });
  try {
    let response = await updateCourseCoursesMasterAPI(courseId, dataToSend);

    let courses = response.data?.courses;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_COURSE_FULFILLED,
      payload: {
        courses,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const deleteCourseCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_COURSE });
  try {
    let response = await deleteCourseCoursesMasterAPI(courseId);

    let courses = response.data?.courses;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_COURSE_FULFILLED,
      payload: {
        courses,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const duplicateCourseCoursesMasterAction = (courseId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DUPLICATE_COURSE });
  try {
    let response = await duplicateCourseCoursesMasterAPI(courseId, dataToSend);

    let courses = response.data?.courses;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DUPLICATE_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DUPLICATE_COURSE_FULFILLED,
      payload: {
        courses,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseDuplicatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DUPLICATE_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const exportCourseCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.EXPORT_COURSE });
  try {
    let response = await exportCourseCoursesMasterAPI(courseId);
    let title = response.headers["x-content-namefile"];

    if (response.status !== 200) {
      dispatch({ type: actionTypes.EXPORT_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.EXPORT_COURSE_FULFILLED,
    });
    
    downloadBlob(response.data, getFileExtension(title), getFileName(title));
    dispatch(openSnackbarAction({ msg: getLocalizedString("courseExportedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.EXPORT_COURSE_REJECTED });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

export const importCourseCoursesMasterAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.IMPORT_COURSE });
  try {
    let response = await importCourseCoursesMasterAPI(dataToSend);

    let courses = response.data?.courses;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.IMPORT_COURSE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.IMPORT_COURSE_FULFILLED,
      payload: {
        courses,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("courseImportedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.IMPORT_COURSE_REJECTED, payload: e });
    errorHandler(e, dispatch, "course");
    return e.response;
  }
};

// MODULES ///////////////////////////////////////////////////////////////////////////

export const getCourseModulesCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_MODULES });
  try {
    let response = await getCourseModulesCoursesMasterAPI(courseId);

    let modules = response.data?.modules;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_MODULES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_MODULES_FULFILLED,
      payload: {
        modules,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_MODULES_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const createCourseModuleCoursesMasterAction = (courseId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_MODULE });
  try {
    let response = await createCourseModuleCoursesMasterAPI(courseId, dataToSend);

    let modules = response.data?.modules;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const updateCourseModuleCoursesMasterAction = (courseId, moduleId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_MODULE });
  try {
    let response = await updateCourseModuleCoursesMasterAPI(courseId, moduleId, dataToSend);

    let modules = response.data?.modules;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const deleteCourseModuleCoursesMasterAction = (courseId, moduleId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_MODULE });
  try {
    let response = await deleteCourseModuleCoursesMasterAPI(courseId, moduleId);

    let modules = response.data?.modules;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_MODULE_FULFILLED,
      payload: {
        modules,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

// ACTIVITIES ///////////////////////////////////////////////////////////////////////////

export const getCourseActivitiesCoursesMasterAction = (courseId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ACTIVITIES });
  try {
    let response = await getCourseActivitiesCoursesMasterAPI(courseId);

    let activities = response.data?.activities;
    let media = response.data?.media;
    let activityTypes = response.data?.activityTypes;
    let questionTypes = response.data?.questionTypes;
    let sessionTypes = response.data?.sessionTypes;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ACTIVITIES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ACTIVITIES_FULFILLED,
      payload: {
        activities,
        media,
        activityTypes,
        questionTypes,
        sessionTypes,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ACTIVITIES_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const getCourseActivityCoursesMasterAction = (courseId, activityId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ACTIVITY });
  try {
    let response = await getCourseActivityCoursesMasterAPI(courseId, activityId);

    let activity = response.data?.activity;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_ACTIVITY_FULFILLED,
      payload: {
        activity,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const createCourseActivityCoursesMasterAction = (courseId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_ACTIVITY });
  try {
    let response = await createCourseActivityCoursesMasterAPI(courseId, dataToSend);

    let activities = response.data?.activities;
    let media = response.data?.media;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const updateCourseActivityCoursesMasterAction = (courseId, activityId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_ACTIVITY });
  try {
    let response = await updateCourseActivityCoursesMasterAPI(courseId, activityId, dataToSend);

    let activities = response.data?.activities;
    let media = response.data?.media;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};

export const deleteCourseActivityCoursesMasterAction = (courseId, activityId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_ACTIVITY });
  try {
    let response = await deleteCourseActivityCoursesMasterAPI(courseId, activityId);

    let activities = response.data?.activities;
    let media = response.data?.media;
    let status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_ACTIVITY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_ACTIVITY_FULFILLED,
      payload: {
        activities,
        media,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("activityDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_ACTIVITY_REJECTED, payload: e });
    errorHandler(e, dispatch, "activity");
    return e.response;
  }
};